import React, { useEffect, useState } from "react";
import {
  Paper,
  Box,
  Container,
  Typography,
  Avatar,
  Grid,
  TextField,
  IconButton,
  MenuItem,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import { useTeams } from "../../../contexts/Teams";
import { useAuth } from "../../../contexts/Auth";
import { LOADING, SUCCESS } from "../../../constants/common";
import { useCommon } from "../../../contexts/common";
import { geocodeAddress } from "../../../services/common.services";
import { zodResolver } from "@hookform/resolvers/zod";
import { invitedUserValidationSchema } from "../../../validations/registerValidation";
import { Controller, useForm, useWatch } from "react-hook-form";
import PlaceAutoComplete from "../../PlaceAutoComplete";
import { InputAdornment } from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { ReactComponent as ChevronDown } from "../../../assets/Icons/chevron-down.svg";
import { trackClickEvent } from "../../../mixpanel";
import CustomButton from "../../CustomButton";
import { useAlert } from "../../../contexts/Alert";
import paths from "../../../constants/paths";

const Register = ({ isLicensed = true, role = null }) => {
  const { signInSSO } = useAuth();
  const { organizationData, next, invitationState, updateInvitationState } = useTeams();
  const { sendAlert } = useAlert();
  const { statesList, fetchStatesList } = useCommon();
  const { checkIfEmailExists } = useCommon();
  const [showPassword, setShowPassword] = useState(false);
  const [countryState, setCountryState] = useState(null);
  const [isDisabled, setIsDisabled] = useState(false);

  const getValidationSchema = () => {
    let schema;

    if (isLicensed) {
      schema = invitedUserValidationSchema;
    } else {
      schema = invitedUserValidationSchema.omit({
        address1: true,
        city: true,
        state: true,
        zip_code: true,
      });
    }

    // If SSO onboarding, remove password validation
    if (invitationState?.sso_onboarding) {
      return schema.omit({ password: true });
    }

    return schema;
  };

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    setValue,
    trigger,
    reset,
  } = useForm({
    resolver: zodResolver(getValidationSchema()),
    mode: "onChange",
    defaultValues: {
      first_name: invitationState.first_name || "",
      last_name: invitationState.last_name || "",
      email: invitationState.email || "",
      password: invitationState.password || "",
      address1: invitationState.address1 || "",
      city: invitationState.city || "",
      state: invitationState.state || 0,
      zip_code: invitationState.zip_code || "",
    },
  });

  const handleSSOLogin = async () => {
    setIsDisabled(true);
    try {
      const { redirect_url } = await signInSSO();
      window.location.href = redirect_url;
    } catch (error) {
      console.error(error);
      setIsDisabled(false);
    }
  }

  const formValues = useWatch({ control });

  useEffect(() => {
    updateInvitationState(formValues);
  }, [formValues]);

  useEffect(() => {
    fetchStatesList();
  }, []);

  useEffect(() => {
    if (
      invitationState.first_name ||
      invitationState.last_name ||
      invitationState.address1
    ) {
      reset({
        first_name: invitationState.first_name || "",
        last_name: invitationState.last_name || "",
        email: invitationState.email || "",
        password: invitationState.password || "",
        address1: invitationState.address1 || "",
        city: invitationState.city || "",
        state: invitationState.state || 0,
        zip_code: invitationState.zip_code || "",
      });
    }
  }, []);

  useEffect(() => {
    if (organizationData?.params?.email) {
      updateInvitationState({ email: organizationData?.params?.email });
      setValue("email", organizationData?.params?.email);
    }
  }, [organizationData?.params?.email]);

  useEffect(() => {
    const updateState = async () => {
      if (countryState && isLicensed) {
        const stateId = statesList.find((st) => st.name === countryState)?.id;
        setValue("state", stateId);
        await trigger("state");
      }
    };
    updateState();
  }, [countryState, isLicensed]);

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const handleAddressChange = async (address) => {
    if (!isLicensed) return;

    const streetAddress = address?.location?.split(",")[0] || "";
    setCountryState(address?.state);

    setValue("address1", streetAddress);
    setValue("city", address.city);
    setValue("zip_code", address.zipCode);
    updateInvitationState({
      address1: streetAddress,
      city: address.city,
      zip_code: address.zipCode,
      lat: address.lat,
      lng: address.lng,
    });
    await trigger();
  };

  const onSubmit = async () => {
    setIsDisabled(true);
    const emailToCheck =
      organizationData?.params?.email || invitationState.email;
    const response = await checkIfEmailExists(emailToCheck);

    if (invitationState?.sso_onboarding) {
      if (isLicensed) {
        const { address1, city, state: stateId, zip_code } = formValues;
        const stateName = statesList.find((st) => st.id === stateId)?.name;
        const addressString = [address1, city, stateName, zip_code]
          .filter(Boolean)
          .join(", ");

        if (addressString) {
          const geocodedAddress = await geocodeAddress(addressString);
          if (geocodedAddress) {
            updateInvitationState({
              lat: geocodedAddress.lat,
              lng: geocodedAddress.lng,
            });
          }
        }
      }

      if (!isLicensed) {
        updateInvitationState({
          address1: "",
          city: "",
          state: 0,
          zip_code: "",
          lat: null,
          lng: null,
        });
      }

      trackClickEvent("Invitation - Registration", {
        page: "Invitation Onboarding",
        button: "Continue",
        isLicensed,
        role,
        sso_onboarding: true,
      });
      setIsDisabled(false);
      next();
      return;
    }

    if (isValid && !Boolean(response.data.code)) {
      // For licensed users, geocode address
      if (isLicensed) {
        const { address1, city, state: stateId, zip_code } = formValues;
        const stateName = statesList.find((st) => st.id === stateId)?.name;
        const addressString = [address1, city, stateName, zip_code]
          .filter(Boolean)
          .join(", ");

        if (addressString) {
          const geocodedAddress = await geocodeAddress(addressString);
          if (geocodedAddress) {
            updateInvitationState({
              lat: geocodedAddress.lat,
              lng: geocodedAddress.lng,
            });
          }
        }
      }

      // Clear address fields for non-licensed users
      if (!isLicensed) {
        updateInvitationState({
          address1: "",
          city: "",
          state: 0,
          zip_code: "",
          lat: null,
          lng: null,
        });
      }

      trackClickEvent("Invitation - Registration", {
        page: "Invitation Onboarding",
        button: "Continue",
        isLicensed,
        role,
      });
      setIsDisabled(false);
      next();
    } else {
      sendAlert("Link has been expired.", "error");
      setIsDisabled(false);
      window.location.href = paths.login;
    }
  };

  return (
    <Container maxWidth="sm">
      <Paper
        sx={{
          textAlign: "center",
          borderRadius: 2,
        }}
      >
        <Box
          sx={{
            backgroundColor: "primary.light",
            pt: 4,
            px: 4,
            pb: 3,
            borderTopLeftRadius: 8,
            borderTopRightRadius: 8,
          }}
        >
          <Typography variant="h1" maxWidth={386} mx="auto" mb={1}>
            Join {organizationData?.params?.program_name} on Trusted Therapist
            Network
          </Typography>
          <Avatar
            sx={{
              width: 60,
              height: 60,
              mx: "auto",
            }}
            src={organizationData?.params?.program_logo}
          />
        </Box>
        <Box
          sx={{
            pt: 3,
            pb: 4,
            px: 4,
            display: "flex",
            flexDirection: "column",
            gap: 3,
          }}
          component="form"
          onSubmit={handleSubmit(onSubmit)}
        >
          {organizationData?.params?.email && (
            <Typography variant="body1">
              You're accepting an invitation sent to{" "}
              {organizationData?.params?.email}.
            </Typography>
          )}
          <Grid container spacing={3}>
            <Grid item xs={6}>
              <Controller
                name="first_name"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    variant="outlined"
                    label="First Name"
                    fullWidth
                    error={!!errors.first_name}
                    helperText={errors.first_name?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={6}>
              <Controller
                name="last_name"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    variant="outlined"
                    label="Last Name"
                    fullWidth
                    error={!!errors.last_name}
                    helperText={errors.last_name?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="email"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    variant="outlined"
                    label="Email"
                    type="email"
                    fullWidth
                    onChange={(e)=>{
                      setValue("email", e.target.value)
                    }}
                    // disabled
                  />
                )}
              />
            </Grid>

            {/* Address fields - only show for licensed users */}
            {isLicensed && (
              <>
                <Grid item xs={12}>
                  <PlaceAutoComplete
                    name="address1"
                    variant="outlined"
                    label="Street address"
                    fullWidth
                    onChange={handleAddressChange}
                    error={!!errors.address1}
                    helperText={errors.address1?.message}
                    control={control}
                    defaultValue={invitationState.address1 || ""}
                  />
                </Grid>
                <Grid item xs={4}>
                  <Controller
                    name="city"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        variant="outlined"
                        label="City"
                        fullWidth
                        error={!!errors.city}
                        helperText={errors.city?.message}
                        onChange={(e) => {
                          const { value } = e.target;
                          field.onChange(value);
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={4}>
                  <Controller
                    name="state"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        select
                        variant="outlined"
                        label="State"
                        fullWidth
                        error={!!errors.state}
                        helperText={errors.state?.message}
                        SelectProps={{
                          IconComponent: () => (
                            <Box
                              sx={{
                                minWidth: 24,
                                width: 24,
                                height: 24,
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "end",
                                mr: 2,
                              }}
                            >
                              <ChevronDown width={12} height={6} />
                            </Box>
                          ),
                          MenuProps: {
                            PaperProps: {
                              style: {
                                maxHeight: 220,
                                width: 200,
                              },
                            },
                          },
                          sx: {
                            "& .MuiSelect-select": {
                              textAlign: "left",
                              paddingLeft: 2,
                            },
                          },
                        }}
                      >
                        <MenuItem value={0} disabled>
                          Select a state
                        </MenuItem>
                        {statesList.map((item) => (
                          <MenuItem key={item.id} value={item.id}>
                            {item.name}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  />
                </Grid>
                <Grid item xs={4}>
                  <Controller
                    name="zip_code"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        variant="outlined"
                        label="ZIP code"
                        fullWidth
                        error={!!errors.zip_code}
                        helperText={errors.zip_code?.message}
                      />
                    )}
                  />
                </Grid>
              </>
            )}
            {
              !invitationState?.sso_onboarding &&
              (
                <Grid item xs={12}>
                  <Controller
                    name="password"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        variant="outlined"
                        type={showPassword ? "text" : "password"}
                        label="Create Your Password"
                        fullWidth
                        error={!!errors.password}
                        helperText={errors.password?.message}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                aria-label="toggle password visibility"
                                onClick={handleClickShowPassword}
                                onMouseDown={handleMouseDownPassword}
                                edge="end"
                              >
                                {showPassword ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    )}
                  />
                </Grid>
              )
            }
          </Grid>
          <LoadingButton
            size="large"
            fullWidth
            variant="contained"
            type="submit"
            loading={false}
            disabled={isDisabled}
          >
            Continue
          </LoadingButton>
          {/* <Grid item xs={12}> */}
          {!invitationState?.sso_onboarding && (
            <CustomButton
              fullWidth
              variant="contained"
              size="large"
              type="submit"
              disabled={isDisabled}
              onClick={handleSSOLogin}
              name="Login with SSO"
              eventName="Sign-in-sso button clicked"
            >
              Continue with SSO
            </CustomButton>
          )}
          {/* </Grid> */}
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              maxWidth: 300,
              mx: "auto",
              textAlign: "center",
              my: 2,
              fontWeight: "bold",
            }}
          >
            By signing up, you agree to the{" "}
            <a
              sx={{ color: "#0C8CE9 !important" }}
              href={`${window.location.origin}/public/terms`}
              target="_blank"
              rel="noopener noreferrer"
            >
              Terms and Conditions
            </a>{" "}
            and{" "}
            <a
              sx={{ color: "#0C8CE9 !important" }}
              href={`${window.location.origin}/public/privacy`}
              target="_blank"
              rel="noopener noreferrer"
            >
              Privacy Policy
            </a>
            .
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
};

export default Register;
